import Link from 'next/link'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { useFetchAllUserOffersQuery } from 'api/productTemplateListsApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { headerNavigationTrackEvent } from 'mParticle/headerNavigationTrackEvent'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useAuth } from 'shared/hooks/useAuth'
import { useUser } from 'shared/hooks/useUser'
import { fonts } from 'shared/lib'
import { BodyText } from 'shared/text'
import { colors } from 'shared/theme'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
  hasActiveOffers,
  hasExpiringOffers,
  setMyAccountViewOffersShortcut,
} from 'store/offersSlice'
import { NavThemeNames } from './Navigation'
import HoverIntent from './components/HoverIntent'
import { UnstyledLink } from './components/MenuLink'

const TP = 'layout.navigation.accountMenu'

type AccountMenuProps = {
  closeMenu: () => void
  navTheme: NavThemeNames
}

type AccountMenuNavThemes = Partial<NavThemeNames> | 'scrolled'

const accountMenuActiveThemes = new Set<AccountMenuNavThemes>(['default', 'overlayed', 'scrolled'])

const AccountMenu = ({ closeMenu, navTheme }: AccountMenuProps) => {
  const isEditorialsEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EDITORIALS)
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const [isOpen, setIsOpen] = useState(false)
  const { currentUser, isAuthenticated } = useUser()
  const { logoutUser } = useAuth()
  const { country: selectedCountryCode } = useShoppingRegionContext()
  const isShoppingRegionUS = selectedCountryCode === 'US'
  const showAsAuthenticated = isAuthenticated && !currentUser?.isGuest
  const hasUserExpiringOffers = useAppSelector((state) => hasExpiringOffers(state))
  const hasUserActiveOffers = useAppSelector((state) => hasActiveOffers(state))
  const dispatch = useAppDispatch()
  const { isSuccess: isSuccessOffers } = useFetchAllUserOffersQuery(
    {},
    {
      skip: !isAuthenticated,
      refetchOnMountOrArgChange: 10,
    },
  )

  const handleHover = () => {
    if (!accountMenuActiveThemes.has(navTheme)) {
      closeMenu()
    }
    setIsOpen(true)
  }

  const handleClickSignOut = async (e) => {
    headerNavigationTrackEvent(e)
    await logoutUser()
  }

  const handleMyOffersClick = () => {
    dispatch(setMyAccountViewOffersShortcut(true))
  }

  useEffect(() => {
    if (!accountMenuActiveThemes.has(navTheme)) {
      setIsOpen(false)
    }
  }, [navTheme])

  useEffect(() => {
    if (currentUser?.isGuest) {
      const logout = async () => await logoutUser()
      logout()
    }
  }, [currentUser])

  return (
    <HoverIntent onEnter={handleHover} onExit={() => setIsOpen(false)}>
      <Container>
        <Link href="/my-account" passHref legacyBehavior>
          <AccountLink
            $isEditorialsEnabled={isEditorialsEnabled}
            data-qa="DesktopNavigationAccountMenu"
            isOpen={isOpen}
            onClick={(e) => headerNavigationTrackEvent(e)}
            onFocus={handleHover}
          >
            {t(`${TP}.account`, 'Account')}
          </AccountLink>
        </Link>
        <Menu isOpen={isOpen}>
          <Link href="/my-account" passHref legacyBehavior>
            <StyledA onClick={(e) => headerNavigationTrackEvent(e)}>
              <MenuLink data-qa="DesktopNavigationAccountMenuMyAccountLink">
                {t(`${TP}.myAccount`, 'My Account')}
              </MenuLink>
            </StyledA>
          </Link>

          {isOffersEnabled && (isShoppingRegionUS || hasUserActiveOffers) && (
            <StyledA onClick={handleMyOffersClick}>
              <OffersLink data-qa="DesktopNavigationMyOffersMyAccountLink">
                {isSuccessOffers && hasUserExpiringOffers && <OffersExpiringIndicator />}
                {t(`${TP}.myOffers`, 'My Offers')}
              </OffersLink>
            </StyledA>
          )}
          {showAsAuthenticated ? (
            <Link href="/my-account" passHref legacyBehavior>
              <StyledA onClick={(e) => headerNavigationTrackEvent(e)}>
                <MenuLink data-qa="DesktopNavigationAccountMenuMyOrdersLink">
                  {t(`${TP}.myOrders`, 'My Orders')}
                </MenuLink>
              </StyledA>
            </Link>
          ) : (
            <Link href="/order-search" passHref legacyBehavior>
              <StyledA onClick={(e) => headerNavigationTrackEvent(e)}>
                <MenuLink data-qa="DesktopNavigationAccountMenuTrackOrderLink">
                  {t(`${TP}.myOrders`, 'My Orders')}
                </MenuLink>
              </StyledA>
            </Link>
          )}
          <Link href="https://support.flightclub.com/hc/en-us" passHref legacyBehavior>
            <StyledA onClick={(e) => headerNavigationTrackEvent(e)}>
              <MenuLink data-qa="DesktopNavigationAccountMenuSupportLink">
                {t(`${TP}.support`, 'Support')}
              </MenuLink>
            </StyledA>
          </Link>
          {showAsAuthenticated ? (
            <LastMenuLink
              data-qa="DesktopNavigationAccountMenuSignOutLink"
              onClick={handleClickSignOut}
            >
              {t(`${TP}.signOut`, 'Sign Out')}
            </LastMenuLink>
          ) : (
            <Link href="/customer/account/login" passHref legacyBehavior>
              <StyledA onClick={(e) => headerNavigationTrackEvent(e)}>
                <LastMenuLink data-qa="DesktopNavigationAccountMenuSignInLink">
                  {t(`${TP}.signUp`, 'Sign Up')}
                </LastMenuLink>
              </StyledA>
            </Link>
          )}
        </Menu>
      </Container>
    </HoverIntent>
  )
}

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 40px;
  position: relative;
`

const AccountLink = styled.a<{ $isEditorialsEnabled: boolean; isOpen: boolean }>`
  color: ${(props) => props.theme.textColor};
  cursor: pointer;
  text-decoration: none;

  ${({ $isEditorialsEnabled }) =>
    $isEditorialsEnabled
      ? css`
          ${fonts.STANDARD};
          font-size: 12px;
          font-weight: 500;
          line-height: 120%;
          text-transform: uppercase;
        `
      : css`
          font-size: 16px;
          font-weight: 500;
        `};
`

const StyledA = styled(UnstyledLink)`
  &:first-child {
    ${(props) =>
      props.theme.backgroundColor === colors.transparent &&
      css`
        margin-top: -15px;
      `}
  }

  &:last-child {
    padding-bottom: 30px;
  }
`

export const MenuLink = styled(BodyText)`
  display: block;
  text-align: right;
  color: ${(props) => props.theme.textColor};
  white-space: nowrap;
  padding: 8px 0;
  cursor: pointer;

  &:hover {
    color: ${(props) =>
      props.theme.textColor === colors.white ? colors.lighterGray : colors.darkGray};
  }
`

const LastMenuLink = styled(MenuLink)`
  padding-bottom: 25px;
`

const Menu = styled.nav<{ isOpen: boolean }>`
  position: absolute;
  top: 65px;
  right: 0;
  max-height: 0;
  min-width: 170px;
  overflow: hidden;
  transition: max-height 200ms ease-out;
  background-color: ${(props) => props.theme.backgroundColor};

  ${(props) =>
    props.isOpen &&
    css`
      max-height: 300px;
    `}

  a:first-child > span {
    padding-top: 0;
  }
`

const OffersExpiringIndicator = styled.div`
  background-color: ${colors.red};
  height: 4px;
  width: 4px;
  margin: 8px 5px 0 2px;
`

const OffersLink = styled(MenuLink)`
  display: flex;
  justify-content: flex-end;
`

export default AccountMenu
